/**
 * ticketExpiry.ts
 *
 * Determines whether a ticket's sales period has already ended, so the
 * edit UI can lock it down. Tickets that don't set a custom end purchase
 * period inherit the event's end date/time as their effective expiry —
 * mirroring how the API derives `expired_at` on the backend.
 */

export interface TicketExpiryInput {
  has_end_purchase_period?: boolean;
  sales_end_date?: string;
  sales_end_time?: string;
}

export function isTicketExpired(
  ticket: TicketExpiryInput,
  eventEndDate?: string,
  eventEndTime?: string,
): boolean {
  const date = ticket.has_end_purchase_period ? ticket.sales_end_date : eventEndDate;
  const time = ticket.has_end_purchase_period ? ticket.sales_end_time : eventEndTime;
  if (!date || !time) return false;

  const expiry = new Date(`${date}T${time}`);
  if (Number.isNaN(expiry.getTime())) return false;

  return expiry.getTime() <= Date.now();
}

/**
 * Calculates event duration in decimal hours based on start/end dates and times.
 * Dates format: YYYY-MM-DD, Times format: HH:mm
 */
export function getEventDurationHours(
  startDate?: string,
  startTime?: string,
  endDate?: string,
  endTime?: string,
): number {
  if (!startDate || !startTime || !endDate || !endTime) return 0;

  const start = new Date(`${startDate}T${startTime}`);
  const end = new Date(`${endDate}T${endTime}`);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;

  const diffMs = end.getTime() - start.getTime();
  return diffMs / (1000 * 60 * 60);
}
